import React from 'react';
import { create } from 'zustand';
import {
  IconBookmarkSolid, IconNoteSolid, IconInfoLine, IconFlagSolid, IconRssSolid, IconToggleEndLine,
  IconEmptyLine,
  IconEmptySolid,
  IconOvalHalfLine,
  IconCheckMarkIndeterminateLine,
  IconArrowOpenEndLine,
  IconArrowDoubleEndLine
} from '@instructure/ui';
import moment from 'moment';
import config from '../config';
import CONSTANTS from '../config/constants';
import { computeMenuPosition } from '@livekit/components-core';

const smartSpeedName = 'Smart Speed';

// Throttle tracking for currentTime updates
let lastCurrentTimeUpdate = 0;
const CURRENT_TIME_THROTTLE_MS = 1000; // Update once per second at normal speed
const DEFAULT_SMART_SPEED_PLAYBACK_SPEED = 4;

/**
 * Get dynamic throttle interval based on playback speed.
 * Higher speeds need more frequent updates for smooth UI.
 * @param {number} playbackSpeed - Current playback speed multiplier
 * @returns {number} Throttle interval in milliseconds
 */
const getThrottleInterval = (playbackSpeed) => {
  if (playbackSpeed >= 10) return 100;  // 10fps UI updates at 10x+ speed
  if (playbackSpeed >= 5) return 200;   // 5fps UI updates at 5x-10x speed
  if (playbackSpeed >= 2) return 500;   // 2fps UI updates at 2x-5x speed
  return 1000; // 1fps UI updates at normal speed (performance optimization)
};

export const createPlayerStore = (initialState = {}) => {
  const store = create((set, get) => ({
    stores: {},
    isLive: false, // If the player is currently showing live video, this is a read only option that is only updated when the video source changes...
    canHaveLiveVideo: false, // If this session can actually have live video, this is passed in from the ReactSession component settings...
    canHaveRecordedVideo: true, //If the session can have recorded video, this is passed in from the ReactSession component settings...
    playing: false, // If the recorded video as playing or is paused.
    activeSessionTime: null, // if in progress, this is the total session time (video)
    activeSessionTimer: null, // an instance of the actual counter/timer for the session...
    currentTime: 0, // The current time of the HLS playback...
    playbackSpeed: 1, // Current speed of the recorded playback...
    suggestedPlaybackSpeed: 1, // The active playback speed that is being used, if smart speed is used this is dynamic based on metrics...
    smartSpeedEnabled: false, // Whether smart speed mode is active...
    smartSpeedPlaybackRanges: [], // these are the speed ranges for smart speed...
    smartSpeedName: smartSpeedName,
    activeVideoSource: config.video.default, // the source of the video that is being displayed...
    dragging: false, // If the playhead on the playbar is being dragged/scrubbed...
    scrubTime: null, // As the playhead is being dragged/scrubbed, the time that is currently
    duration: null, // The duration of the actual HLS stream....
    volume: 50,
    muted: false,
    annotationContextMenu: null,
    overlay: CONSTANTS.OVERLAYS.PLAYBACK_COVER, // the current overlay that should be displayed on the video player...
    hlsVideoRef: null, // ref to the HLS video DOM item...
    viewRawHlsVideo: false, // if the video that is displayed should be the raw HLS video or integrated viewer...
    seekCounter: 0, // Incremented on each seek to force component remount
    hlsPlaybackReady: false, // if the HLS video is ready to play, which will include the first frame loaded...
    isSeeking: false, // if the video is currently seeking to a new position...
    fullscreen: false, // if the video is being played back in full screen mode...
    secondsBehindLive: null, // this is the number of seconds the end of the recording is behind the live video...this only applies when the session is in progress.
    showHoverControls: false, // If the hover controls should be shown...
    focusedTrackId: null, // This is the Id of the track that is currently focused, this will help us ensure we are always showing the correct track when flipping between live and recorded video...
    defaultFocusedTrackSource: CONSTANTS.TRACK_NAMES.SCREEN_SHARE, // If there is no focused track, this will ensure we select the correct one. Valid options include: webcam, screen-share, second-camera
    defaultFocusedParticipantType: 'student',
    videoPanelSplitPercentage: CONSTANTS.PLAYER.DEFAULT_SIDEBAR_PCT,
    showSidebar: false, // if the sidebar should show or not...
    sidebarTracks: {}, // this is the tracks that are currently shown in the sidebar, essentially all tracks but the focused one...
    sidebarTracksAtTime: 0, // how many tracks are in the sidebar at the current time...
    showHlsViewerDebug: false,
    annotationsLiveTimeline: {
      maxExamLength: 28800, // max exam length is 8hr. This number is in seconds.
      perSecondWidth: 3, // this is the number of pixels that represent each second.
      displayWindowWidth: 500, //number of pixels wide the timeline display window is, this will help inform us how we have to scroll...
    },
    tracks: {},
    trackStatusByIdentity: {},
    tracksByIdentity: {},
    hasTracks: false, // this is a boolean we can use to know if we have tracks, will be better than pulling in tracks object which would trigger a re-render when tracks change..
    markers: [
      {
        label: 'Flags',
        id: 'flags',
        type: 'point',
        icon: <IconFlagSolid />,
      },
      { label: 'Events', id: 'events', type: 'range', icon: <IconInfoLine /> },
      { label: 'Phases', id: 'phases', type: 'range', icon: <IconToggleEndLine /> },
      //{ label: 'Connection', id: 'connection', type: 'range', icon: <IconRssSolid /> },
      // remove notes for now...
      //{ label: 'Notes', id: 'notes', type: 'point', icon: <IconNoteSolid /> },
    ],
    markersDisplayProperties: {
      "events": {
        height: CONSTANTS.PLAYER.MARKER_DEFAULT_ROW_HEIGHT
      },
      "phases": {
        height: CONSTANTS.PLAYER.MARKER_DEFAULT_ROW_HEIGHT
      },
      "connection": {
        height: CONSTANTS.PLAYER.MARKER_DEFAULT_ROW_HEIGHT
      },
      "flags": {
        height: CONSTANTS.PLAYER.MARKER_DEFAULT_ROW_HEIGHT
      }
    },
    speedOptions: [
      { value: 0.5, label: '0.5x', key: 'slow' },
      { value: 1.0, label: 'Normal', key: 'normal' },
      { value: 2.0, label: '2x', key: 'fast_2X' },
      { value: 5.0, label: '5x', key: 'fast_5X' },
      { value: 10.0, label: '10x', key: 'fast_10X' },
      //{ value: 16.0, label: '16x', key: 'fast_16X' }, //Getting rid of this b / c it is too fast for buffering...
      { value: 100.0, label: smartSpeedName, key: 'smart' },
    ],
    displayMarkers: ['flags'], // The display markers that are currently active...
    displayMarkersUpdated: false,
    displayVideoLabels: "hide", // by default hide the video labels

    /**
     * Converts an annotation score to a color indicator
     * @param {number} score - The annotation score
     * @returns {string} Color string: 'danger', 'warning', or 'primary'
     */
    flagScoreToSeverity: (score) => {
      if (score > 7) {
        return { color: "danger", iconColor: 'white', backgroundColor: '#E62429', icon: <IconArrowDoubleEndLine rotate={270} /> };
      } else if (score > 3) {
        return { color: "primary", iconColor: 'black', backgroundColor: '#fecf4bff', icon: <IconArrowOpenEndLine rotate={270} /> };
      } else {
        return { color: "secondary", iconColor: 'black', backgroundColor: '#D7DADE', icon: <IconCheckMarkIndeterminateLine /> };
      }
    },


    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    setMarkersDisplayProperty: (markerId, property, value) => {
      set({
        markersDisplayProperties: {
          ...get().markersDisplayProperties,
          [markerId]: {
            ...get().markersDisplayProperties[markerId],
            [property]: value,
          },
        },
      });
    },

    /**
     * Initializes the player store by loading saved user preferences and starting necessary timers.
     * This function restores the player's state from localStorage and sets up the initial
     * configuration based on the current video source and saved user settings.
     * 
     * Initialization Process:
     * 1. Loads saved playback annotations/markers from localStorage
     * 2. Restores user's preferred playback speed setting
     * 3. Restores video panel split percentage for sidebar layout
     * 4. Starts the active session timer if in live mode
     * 
     * Persistence Features:
     * - Playback annotations: Saved marker visibility preferences
     * - Playback speed: User's preferred video playback rate
     * - Video split percentage: Sidebar width preference
     * 
     * Live Mode Handling:
     * - Automatically starts the session timer for live video tracking
     * - Ensures proper time synchronization for live sessions
     * 
     * @function initialize
     * @returns {void} This function does not return a value but updates multiple state properties
     * 
     * @example
     * // Initialize the player store on component mount
     * useEffect(() => {
     *   playerStore.initialize();
     * }, []);
     * 
     * @example
     * // Initialize after store creation
     * const playerStore = createPlayerStore();
     * playerStore.initialize();
     * 
     * @see {@link startActiveSessionTimer} For live session timer management
     * @see {@link formatSpeedValue} For speed value validation and formatting
     */
    initialize: () => {
      // Restore saved playback annotations/markers from localStorage
      const storedAnnotations = localStorage.getItem('playbackAnnotations');
      if (storedAnnotations) {
        try {
          const savedDisplayAnnotations = JSON.parse(storedAnnotations);
          // regardless, always add flags as we want to always show that...
          if (!savedDisplayAnnotations.includes('flags')) {
            savedDisplayAnnotations.push('flags');
          }
          // TODO: Commented this out because we are not going to use them right now.
          set({ displayMarkers: savedDisplayAnnotations });
        } catch (error) {
          console.warn('Failed to parse stored annotations:', error);
        }
      }

      // Restore user's preferred playback speed setting
      const storedSpeed = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.PLAYBACK_SPEED);
      if (storedSpeed) {
        get().setPlaybackSpeed(storedSpeed);
      }

      const storedVideoLabels = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.DISPLAY_VIDEO_LABELS);
      if (storedVideoLabels) {
        get().setDisplayVideoLabels(storedVideoLabels);
      }

      // Restore video panel split percentage for sidebar layout
      const storedVideoSplitPercentage = localStorage.getItem(
        `${CONSTANTS.LOCALSTORAGE_KEYS.VIDEO_PANEL_SPLIT_PERCENTAGE}_${get().storeState('examSessionStore').sessionSid}`
      );

      if (storedVideoSplitPercentage) {
        set({ videoPanelSplitPercentage: storedVideoSplitPercentage });
      }

      const storedVolumeLevel = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.VOLUME_LEVEL);
      if (storedVolumeLevel) {
        get().setVolume(storedVolumeLevel);
      }

      const storedMuteSetting = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.MUTE_SETTING);
      if (storedMuteSetting) {
        get().setMuted(storedMuteSetting === 'true');
      }

      // Start the active session timer if currently in live mode
      if (get().activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) {
        get().startActiveSessionTimer();
      }
    },


    /**
     * Adds or updates a track in the player's track collection and manages UI state.
     * This function handles both new track additions and updates to existing tracks,
     * automatically managing focus selection and sidebar layout based on the changes.
     * 
     * Process:
     * 1. Creates a shallow copy of existing tracks to avoid mutations
     * 2. If track exists, merges new video data (HLS or WebRTC) with existing track
     * 3. If track is new, adds it to the collection
     * 4. Updates the tracks state and hasTracks flag
     * 5. Attempts to auto-select a focused track if none is set
     * 6. Updates sidebar tracks if the new track is not focused
     * 7. Recalculates sidebar layout and visibility
     * 
     * Track Merging:
     * - Existing tracks are updated with new HLS or WebRTC video data
     * - Other track properties (metadata, status, etc.) remain unchanged
     * - This allows for progressive loading of video streams
     * 
     * Side Effects:
     * - May change the focused track if none was previously set
     * - Updates sidebar track collection and layout
     * - May show/hide sidebar based on track availability
     * - Updates hasTracks flag for conditional rendering
     * - For live WebRTC sessions: Automatically replaces old disconnected tracks with new tracks
     *   from the same participant (handles reconnection with new track IDs)
     * 
     * @function addTrack
     * @param {Object} track - The track object to add or update
     * @param {string} track.id - Unique identifier for the track
     * @param {Object} [track.hls] - HLS video stream data
     * @param {Object} [track.webrtcRef] - WebRTC video reference
     * @param {string} [track.participantType] - Type of participant (e.g., 'student', 'proctor')
     * @param {string} [track.source] - Track source (e.g., 'camera', 'screen-share')
     * @param {string} [track.kind] - Track type (e.g., 'video', 'audio')
     * @returns {void} This function does not return a value but updates multiple states
     * 
     * @example
     * // Add a new student camera track
     * playerStore.addTrack({
     *   id: 'student-camera-1',
     *   participantType: 'student',
     *   source: 'camera',
     *   kind: 'video',
     *   hls: { streamUrl: 'https://...' }
     * });
     * 
     * @example
     * // Update existing track with WebRTC reference
     * playerStore.addTrack({
     *   id: 'existing-track-id',
     *   webrtcRef: webrtcVideoElement
     * });
     * 
     * @example
     * // Add a new WebRTC track (will replace old track from same participant if exists)
     * playerStore.addTrack({
     *   id: 'new-track-sid-123',
     *   participantSid: 'participant-abc',
     *   name: 'camera',
     *   source: 'camera',
     *   webrtcRef: trackReference
     * });
     * // If an old track with participantSid='participant-abc' and name='camera' exists,
     * // it will be removed and replaced with this new track
     * 
     * @see {@link autoSetFocusedTrack} For automatic track selection logic
     * @see {@link refreshSidebarTracks} For sidebar track management
     * @see {@link updateSidebarTrackLayout} For layout recalculation
     */
    addTrack: (track) => {
      // Create a shallow copy of tracks to avoid mutating the original state
      const tracks = { ...get().tracks };

      // Track uses stable ID (e.g., "student-123_video_camera")
      // This ID persists across reconnections
      const stableId = track.id;

      // Check if we already have a track with this stable ID
      if (tracks[stableId]) {
        const existingTrack = tracks[stableId];

        // Update existing track with new data
        if (track.hasRecordedData()) {
          existingTrack.updateHLSTrack(track);
        }

        if (track.hasLiveData()) {
          existingTrack.updateWebRTCTrack(track);
        }

        // Track was updated, not added
        // Create a new tracks object to ensure React detects the change
        set({ tracks: { ...tracks }, hasTracks: Object.keys(tracks).length > 0 });

        // Rebuild status lookup with merged statuses
        // This is critical when tracks reconnect with different TR_ IDs
        get().groupTracksByIdentity();

        // Refresh sidebar and layout to show the reconnected track
        // This is important when a track reconnects after being offline
        get().refreshSidebarTracks();
        get().updateSidebarTrackLayout();

        return;
      }

      // New track - add it to the collection
      tracks[stableId] = track;

      // Update the tracks state and availability flag
      set({ tracks, hasTracks: Object.keys(tracks).length > 0 });

      // Attempt to automatically select a focused track if none is currently set
      get().autoSetFocusedTrack();

      // Update sidebar if this track was not selected as the focused track
      if (get().focusedTrackId !== stableId) {
        // Refresh sidebar tracks to include this new non-focused track
        get().refreshSidebarTracks();
      }

      // Recalculate sidebar layout, dimensions, and visibility
      get().updateSidebarTrackLayout();
      get().groupTracksByIdentity();
    },

    /**
     * Determines if a track is online (available) at a specific point in time and automatically
     * switches focus when a replacement track comes online.
     * 
     * This function evaluates track availability by examining the track's status history
     * and finding the most recent status change that occurred before or at the given time.
     * 
     * **Auto-Focus Switching Logic:**
     * When a track returns 'connected' status, the function performs an additional check:
     * - If the currently focused track has the same identity and track name as this track
     * - But has a different track ID (indicating it's a replacement track)
     * - The function automatically switches focus to this newly connected track
     * 
     * This enables seamless switching when participants reconnect with new track IDs.
     * 
     * **Main Logic:**
     * 1. If the session is live, all tracks return 'connected'
     * 2. If no status history exists, returns null
     * 3. Iterates through status changes chronologically to find the last applicable status
     * 4. Maps internal status to connection status: 'online' → 'connected', 'offline' → 'disconnected'
     * 5. Performs auto-focus switching when track comes online
     * 
     * Status Array Format:
     * Each status object should contain:
     * - sessionChangeTime: timestamp when the status changed
     * - status: 'online', 'offline', or 'not-yet-connected'
     * - trackId: ID of the track this status applies to (or null for stable track status)
     * 
     * @function getTrackStatusAtTime
     * @param {Object} SessionTrack - The track object containing status information
     * @param {number} currentTime - The time (in seconds) to check track status for
     * @returns {string|null} The connection status:
     *   - 'connected': Track is online and available (may trigger auto-focus switch)
     *   - 'not-yet-connected': Track hasn't connected yet (before first connection)
     *   - 'disconnected': Track is offline (last status was 'offline')
     *   - null: No status information available or track replaced by another
     * 
     * @example
     * // Check track status at 30 seconds
     * const status = playerStore.getTrackStatusAtTime(sessionTrack, 30);
     * // Returns: 'connected', 'not-yet-connected', 'disconnected', or null
     * 
     * @example
     * // Example usage with different return values
     * const status = playerStore.getTrackStatusAtTime(sessionTrack, 45);
     * if (status === 'connected') {
     *   // Track is available for playback (may have auto-switched focus)
     * } else if (status === 'not-yet-connected') {
     *   // Track hasn't connected yet
     * } else if (status === 'disconnected') {
     *   // Track is offline at this time
     * } else if (status === null) {
     *   // No status information available or track replaced
     * }
     * 
     * @example
     * // Auto-switching scenario:
     * // Current focused track: track-123 (Student Camera, offline)
     * // Checking track: track-456 (Student Camera, same identity, comes online)
     * // Result: Function returns 'connected' AND automatically switches focus to track-456
     * 
     * @example
     * // Example status array structure in SessionTrack
     * const sessionTrack = {
     *   hls: {
     *     track: {
     *       statuses: [
     *         { sessionChangeTime: 0, status: 'online' },
     *         { sessionChangeTime: 60, status: 'offline' },
     *         { sessionChangeTime: 120, status: 'online' }
     *       ]
     *     }
     *   }
     * };
     * 
     * @see {@link autoSetFocusedTrack} Uses this function for track selection
     * @see {@link updateSidebarTrackLayout} Uses this function for layout calculations
     * @see {@link focusTrack} Called automatically when replacement track detected
     */
    getTrackStatusAtTime: (SessionTrack, currentTime) => {
      const isLive = get().isLive;
      const trackStatusByIdentity = get().trackStatusByIdentity;
      const identity = SessionTrack?.participantIdentity;
      const trackName = SessionTrack?.name;

      // Validate input parameters
      if (!trackStatusByIdentity[identity] || !trackStatusByIdentity[identity][trackName]) {
        return null;
      }

      const statuses = trackStatusByIdentity[identity][trackName].statuses;
      if (!Array.isArray(statuses) || statuses.length === 0) return null;

      if (isLive) {
        // In live mode, check if the track has live WebRTC data
        // If it does, return 'connected' to show the track
        // The actual connection/disconnection overlay is managed by the Webrtc component
        // which monitors LiveKit subscription status
        if (SessionTrack.hasLiveData()) {
          // Return 'connected' to indicate the track should be shown
          // The Webrtc component will handle showing disconnected overlay if needed
          return 'connected';
        }

        // Track doesn't have live data - don't show it
        return null;
      }

      // Recorded mode - find status at currentTime
      // Find the most recent status change that occurred before or at currentTime
      let lastStatus = null;
      let nextStatus = null;

      for (let i = 0; i < statuses.length; i++) {
        if (statuses[i].sessionChangeTime <= currentTime) {
          lastStatus = statuses[i];
        } else {
          // This is the next status after currentTime
          nextStatus = statuses[i];
          break;
        }
      }

      // No status found for this time
      if (!lastStatus) {
        return null;
      }

      // Determine display status based on current and next status
      if (lastStatus.status === 'online') {
        // Verify the SessionTrack has HLS data
        // This is important when tracks reconnect with different TR_ IDs
        if (!SessionTrack.hasRecordedData()) {
          return null;
        }
        return 'connected';
      } else if (lastStatus.status === 'not-yet-connected') {
        // Track hasn't connected yet - show placeholder with not-yet-connected overlay
        return 'not-yet-connected';
      } else if (lastStatus.status === 'offline' && nextStatus?.status === 'online') {
        // Show disconnected overlay if track will come back online
        return 'disconnected';
      }

      // Track is offline and won't come back online (or no next status)
      return null;
    },

    /**
     * Groups tracks and their status histories by participant identity.
     * 
     * This function organizes track data to enable efficient lookups by participant identity
     * and track name. It creates two main data structures:
     * 
     * 1. tracksByIdentity: Groups track IDs by identity and track name
     * 2. trackStatusByIdentity: Aggregates all status changes by identity and track name
     * 
     * Data Structure Output:
     * - tracksByIdentity[identity][trackName] = [trackId1, trackId2, ...]
     * - trackStatusByIdentity[identity][trackName] = { statuses: [...] }
     * 
     * Each status entry contains:
     * - identity: Participant identity
     * - trackId: Unique track identifier
     * - status: Connection status ('online' or 'offline')
     * - sessionChangeTime: Time when status changed (seconds)
     * - changeDate: Date object of when status changed
     * 
     * The status arrays are automatically sorted by sessionChangeTime in ascending order
     * to enable efficient chronological lookups for determining track availability at
     * specific points in time.
     * 
     * Use Cases:
     * - Determine track availability across multiple tracks for the same participant
     * - Find the most recent status for a participant's track type
     * - Aggregate connection history across all tracks for an identity
     * 
     * @function groupTracksByIdentity
     * @returns {void} Updates store state with tracksByIdentity and trackStatusByIdentity
     * 
     * @example
     * // After calling groupTracksByIdentity()
     * const participantTracks = store.tracksByIdentity['participant-123']['video'];
     * const statusHistory = store.trackStatusByIdentity['participant-123']['video'].statuses;
     */
    groupTracksByIdentity: () => {
      const tracks = get().tracks;
      const tracksByIdentity = {};
      const trackStatusByIdentity = {};
      const trackValues = Object.values(tracks);

      // Process each track to group by identity and aggregate status histories
      for (let i = 0; i < trackValues.length; i++) {
        const track = trackValues[i];

        // Skip tracks without required data
        if (!track.hasRecordedData() || !track.hls.participant || !track.hls.track) {
          continue;
        }

        const identity = track.participantIdentity;
        const trackName = track.name;

        // Initialize identity groups once per identity
        if (!tracksByIdentity[identity]) {
          tracksByIdentity[identity] = {};
          trackStatusByIdentity[identity] = {};
        }

        // Initialize track name containers once per identity/trackName combination
        if (!tracksByIdentity[identity][trackName]) {
          tracksByIdentity[identity][trackName] = [];
          trackStatusByIdentity[identity][trackName] = { statuses: [] };
        }

        // Add STABLE track ID (not original HLS ID)
        tracksByIdentity[identity][trackName].push(track.id);

        // Process status history for this track
        const statuses = track.hls.track.statuses;
        const statusArray = trackStatusByIdentity[identity][trackName].statuses;

        for (let j = 0; j < statuses.length; j++) {
          const status = statuses[j];
          // Add status entry with stable track ID
          statusArray.push({
            identity: identity,
            trackId: track.id, // Use stable ID here
            status: status.status,
            sessionChangeTime: status.sessionChangeTime,
            changeDate: status.changeDate
          });
        }
      }

      // Sort all status arrays chronologically
      for (const identity in trackStatusByIdentity) {
        const identityTracks = trackStatusByIdentity[identity];
        for (const trackName in identityTracks) {
          identityTracks[trackName].statuses.sort((a, b) => a.sessionChangeTime - b.sessionChangeTime);
        }
      }

      // Update store state with the organized data structures
      set({ tracksByIdentity, trackStatusByIdentity });
    },

    /**
     * Automatically sets the focused track based on availability and preferences.
     * This function implements a priority-based selection algorithm to determine
     * which video track should be focused when no track is currently selected.
     * 
     * Selection Logic:
     * 1. If a focused track is already set and force is false, no action is taken
     * 2. Prioritizes the default focused track (matching defaultFocusedParticipantType 
     *    and defaultFocusedTrackSource) if it's online at the current time
     * 3. Falls back to the first available video track that is online at the current time
     * 
     * @function autoSetFocusedTrack
     * @param {boolean} [force=false] - If true, will override existing focused track selection
     * @returns {void} This function does not return a value but may update the focusedTrackId state
     * 
     * @example
     * // Call when tracks change or time updates to ensure proper focus
     * playerStore.autoSetFocusedTrack();
     * 
     * @example
     * // Force re-evaluation even if a track is already focused
     * playerStore.autoSetFocusedTrack(true);
     * 
     * @see {@link isTrackOnlineAtTime} For track availability checking
     * @see {@link focusTrack} For the actual track focusing implementation
     */
    autoSetFocusedTrack: (force = false) => {
      const tracks = get().tracks;
      const currentTime = get().currentTime;
      const focusedTrackId = get().focusedTrackId;
      const defaultFocusedParticipantType = get().defaultFocusedParticipantType;
      const defaultFocusedTrackSource = get().defaultFocusedTrackSource;

      // 1. If the focused track is already set, do nothing
      if (focusedTrackId && !force) {
        return;
      }

      // Get all video tracks
      const videoTracks = Object.values(tracks).filter(track => track.kind === 'video');

      // 2. Look for the default focused track that is online at currentTime
      const defaultTrack = videoTracks.find(track =>
        track.participantType === defaultFocusedParticipantType &&
        track.source === defaultFocusedTrackSource &&
        track.hls?.track?.statuses &&
        get().getTrackStatusAtTime(track, currentTime)
      );

      if (defaultTrack) {
        get().focusTrack(defaultTrack.id);
        return;
      }

      // 3. Take the first track that is online at currentTime
      const firstOnlineTrack = videoTracks.find(track =>
        track.hls?.track?.statuses &&
        get().getTrackStatusAtTime(track, currentTime) === 'connected'
      );

      if (firstOnlineTrack) {
        get().focusTrack(firstOnlineTrack.id);
      }
    },

    /**
     * Sets a specific track as the focused track and updates the UI layout accordingly.
     * This function manages the primary video display by designating which track should
     * be shown in the main focused view and triggers necessary sidebar updates.
     * 
     * Process:
     * 1. Updates the focusedTrackId state with the new track ID
     * 2. Refreshes the sidebar tracks to exclude the newly focused track
     * 3. Updates the sidebar layout and dimensions based on remaining tracks
     * 
     * Side Effects:
     * - Changes which track is displayed in the main focused view
     * - Removes the focused track from the sidebar collection
     * - Recalculates sidebar layout and visibility
     * - May trigger sidebar show/hide based on remaining track availability
     * 
     * @function focusTrack
     * @param {string} trackId - The unique identifier of the track to focus
     * @returns {void} This function does not return a value but updates multiple UI states
     * 
     * @example
     * // Focus on a specific track by ID
     * playerStore.focusTrack('track-123');
     * 
     * @example
     * // Called automatically by autoSetFocusedTrack
     * playerStore.autoSetFocusedTrack(); // May internally call focusTrack
     * 
     * @see {@link autoSetFocusedTrack} For automatic track selection logic
     * @see {@link refreshSidebarTracks} For sidebar track filtering
     * @see {@link updateSidebarTrackLayout} For layout recalculation
     */
    focusTrack: (trackId) => {
      // Set the new focused track ID in the state
      set({
        focusedTrackId: trackId
      });

      // Remove the newly focused track from the sidebar collection
      // to prevent it from appearing in both views
      get().refreshSidebarTracks();

      // Recalculate sidebar layout, dimensions, and visibility
      // based on the remaining available tracks
      get().updateSidebarTrackLayout();
    },

    removeTrack: (trackId) => {
      const tracks = { ...get().tracks };
      delete tracks[trackId];
      set({ tracks, hasTracks: Object.keys(tracks).length > 0 });
    },

    /**
     * Sets whether to display the raw HLS video or the integrated viewer.
     * This toggle allows switching between the raw video stream and the 
     * processed viewer with overlays and UI components.
     * 
     * @function setViewRawHlsVideo
     * @param {boolean} viewRawHlsVideo - True to show raw HLS video, false to show integrated viewer
     * @returns {void}
     * 
     * @example
     * // Switch to raw HLS video view
     * PlayerStore.setViewRawHlsVideo(true);
     * 
     * @example
     * // Switch back to integrated viewer
     * PlayerStore.setViewRawHlsVideo(false);
     */
    setViewRawHlsVideo: (viewRawHlsVideo) => {
      set({ viewRawHlsVideo });
    },

    startActiveSessionTimer: () => {
      // Ensure the recording start date is populated and there is not already a timer started...
      if (
        get().storeState('examSessionStore').sessionData.recordingStartDate &&
        !get().activeSessionTimer
      ) {
        // first, we need to update the timer to ensure it starts from the proper spot...
        get().updateActiveSessionTime();
        // set an interval to start the timer...
        get().activeSessionTimer = setInterval(() => {
          set({ activeSessionTime: get().activeSessionTime + 1 });
        }, 1000);
      }
    },
    stopActiveSessionTimer: () => {
      if (get().activeSessionTimer) {
        clearInterval(get().activeSessionTimer);
      }
      // when the active session timer is stopped, the source should also flip to recording...
      set({ activeSessionTimer: null });
    },

    syncCurrentTimeFromPlayer: () => {
      set({ currentTime: get().hlsVideoRef.current.currentTime });
    },

    setShowHoverControls: (showHoverControls) => {
      set({ showHoverControls: showHoverControls });
    },
    setShowSidebar: (showSidebar) => {
      set({ showSidebar: showSidebar });
    },
    updateActiveSessionTime: () => {
      const now = moment.utc();
      const startTime = moment.utc(
        get().storeState('examSessionStore').sessionData.recordingStartDate
      );
      const activeSessionTime = now.diff(startTime, 'seconds');
      set({
        activeSessionTime,
      });
    },

    calculateLiveTimelineWidth: () => {
      const totalWidth =
        get().annotationsLiveTimeline.maxExamLength *
        get().annotationsLiveTimeline.perSecondWidth;
      return `${totalWidth}px`;
    },
    setLiveTimelineWindowWidth: (width) => {
      const liveTimelineInfo = { ...get().annotationsLiveTimeline };
      liveTimelineInfo.displayWindowWidth = width;
      set({ annotationsLiveTimeline: liveTimelineInfo });
    },
    setSecondsBehindLive: (secondsBehindLive) => set({ secondsBehindLive }),
    setTimer: (timer) => set({ timer }),
    setScrubTime: (scrubTime) => set({ scrubTime }),
    setIsSeeking: (isSeeking) => set({ isSeeking }),
    onSeek: () => { },
    onSeekToLive: () => { },
    onSeekToLiveComplete: () => { },
    setHlsPlaybackReady: (hlsPlaybackReady) => set({ hlsPlaybackReady }),
    setVolume: (volume) => {
      const video = get().hlsVideoRef?.current;
      if (video) {
        video.volume = volume / 100;
        get().setMuted(volume === 0);
        localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.VOLUME_LEVEL, volume);
      }
      set({ volume })
    },
    setMuted: (muted) => {
      const video = get().hlsVideoRef?.current;
      if (video) {
        video.muted = muted;
      }

      // Get the LiveKit room from appStore
      const room = get().storeState('appStore')?.room;

      if (room && room.remoteParticipants) {
        if (muted) {
          // Unsubscribe from all remote audio tracks
          room.remoteParticipants.forEach((participant) => {
            participant.audioTrackPublications.forEach((publication) => {
              if (publication.isSubscribed) {
                publication.setSubscribed(false);
              }
            });
          });
        } else {
          // Subscribe to all remote audio tracks
          room.remoteParticipants.forEach((participant) => {
            participant.audioTrackPublications.forEach((publication) => {
              if (!publication.isSubscribed) {
                publication.setSubscribed(true);
              }
            });
          });
        }
      }

      set({ muted });
      localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.MUTE_SETTING, muted);
    },

    // ... (rest of the code remains the same)
    /**
     * Resets the video player state when videos get stuck or corrupted.
     * This function handles cases where video elements enter an unrecoverable state,
     * typically after reaching the end and attempting to seek back.
     * 
     * @function resetVideoState
     * @returns {void} This function does not return a value but resets video state
     */
    resetVideoState: () => {
      const video = get().hlsVideoRef?.current;
      if (video) {
        // Pause the video first
        video.pause();

        // Don't call video.load() as it breaks HLS blob URLs
        // Instead, just reset the currentTime to 0
        video.currentTime = 0;

        // Clear any problematic overlays
        if (get().overlay === CONSTANTS.OVERLAYS.PLAYBACK_COMPLETE) {
          get().setOverlay(null);
        }

        // Reset playback state
        set({
          playbackComplete: false,
          hlsPlaybackReady: false,
          overlay: null,
          seekCounter: 0, // Incremented on each seek to force component remount
        });

        // Wait for the video to be ready again
        const handleCanPlay = () => {
          get().setHlsPlaybackReady(true);
          video.removeEventListener('canplay', handleCanPlay);
        };

        video.addEventListener('canplay', handleCanPlay);
      }
    },

    // create setters for each of the properties
    setActiveVideoSource: (activeVideoSource) => {
      // Ensure the source that was passed in is actually valid...
      if (config.video.validTypes.includes(activeVideoSource)) {
        // ensure that the source being sent is different than the current source.
        if (activeVideoSource !== get().activeVideoSource) {
          // be sure to stop the player in the event is is playing...
          const video = get().hlsVideoRef?.current;

          if (video) {
            // if it is not set to live and it is playing...
            if (
              activeVideoSource === CONSTANTS.VIDEO_SOURCES.RECORDED &&
              !video.paused &&
              !video.ended &&
              video.readyState > 2
            ) {
              video.pause();
            }
          }

          if (activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE) {
            get().startActiveSessionTimer();
            // ensure if the HLS video was playing it is set to stop...
            get().setPlaying(false);
          } else {
            //const duration = get().duration;
            //set({ isLive, duration });
            get().stopActiveSessionTimer();
          }
          set({
            activeVideoSource,
            isLive:
              activeVideoSource === CONSTANTS.VIDEO_SOURCES.LIVE ? true : false,
          });

          // Refresh the sidebar tracks and layout to show the correct tracks for the new video source
          // This ensures live tracks are shown when switching to live, and recorded tracks when switching to recorded
          get().refreshSidebarTracks();
          get().updateSidebarTrackLayout();
        }
      }
    },

    setCanHaveLiveVideo: (canHaveLiveVideo) => {
      set({ canHaveLiveVideo });
      if (canHaveLiveVideo) {
        //get().setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.LIVE);
      }
    },

    setCanHaveRecordedVideo: (canHaveRecordedVideo) => {
      set({ canHaveRecordedVideo });
    },

    /**
     * Refreshes the sidebar tracks by filtering out the currently focused track.
     * This function maintains the sidebar's track collection by ensuring it only contains
     * tracks that are not currently being displayed in the main focused view.
     * 
     * Process:
     * 1. Creates a shallow copy of all available tracks
     * 2. Removes the currently focused track from the collection
     * 3. Updates the sidebarTracks state with the filtered collection
     * 
     * This function should be called whenever:
     * - A new track is focused
     * - Tracks are added or removed from the session
     * - The focused track changes
     * 
     * @function refreshSidebarTracks
     * @returns {void} This function does not return a value but updates the sidebarTracks state
     * 
     * @example
     * // Called automatically when focus changes
     * playerStore.refreshSidebarTracks();
     * 
     * @see {@link focusTrack} For track focusing functionality
     * @see {@link updateSidebarTrackLayout} For layout updates after refresh
     */
    refreshSidebarTracks: () => {
      // Create a shallow copy of all tracks to avoid mutating the original
      const sidebarTracks = { ...get().tracks };

      // Remove the currently focused track from the sidebar collection
      // since it will be displayed in the main focused view
      delete sidebarTracks[get().focusedTrackId];

      // Update the sidebar tracks state with the filtered collection
      set({
        sidebarTracks
      });
    },

    /**
     * Updates the sidebar track layout based on track availability at the current time.
     * This function manages the visibility and layout of the sidebar by counting online tracks
     * and automatically adjusting the UI state accordingly.
     * 
     * Layout Logic:
     * 1. Counts how many sidebar tracks are online at the current playback time
     * 2. If no tracks are online, hides the sidebar and sets split percentage to 0
     * 3. If tracks are online and sidebar is hidden, shows sidebar and resets layout
     * 4. Updates the sidebarTracksAtTime count for UI calculations
     * 
     * Side Effects:
     * - May hide/show the sidebar based on track availability
     * - May reset video panel split percentage when tracks become available
     * - May trigger automatic focused track selection when sidebar becomes visible
     * 
     * @function updateSidebarTrackLayout
     * @returns {void} This function does not return a value but updates multiple state properties
     * 
     * @example
     * // Called automatically when tracks change or time updates
     * playerStore.updateSidebarTrackLayout();
     * 
     * @see {@link isTrackOnlineAtTime} For track availability checking
     * @see {@link setShowSidebar} For sidebar visibility control
     * @see {@link autoSetFocusedTrack} For automatic track selection
     */
    updateSidebarTrackLayout: () => {
      const sidebarTracks = get().sidebarTracks;
      const currentTime = get().currentTime;

      // Count how many sidebar tracks are online at the current time
      let onlineTracks = 0;
      Object.keys(sidebarTracks).forEach((trackId) => {
        const track = sidebarTracks[trackId];
        // An online track is any connected or disconnected track, this will check that...
        if (get().getTrackStatusAtTime(track, currentTime)) {
          onlineTracks++;
        }
      });

      // Handle sidebar visibility based on track availability
      if (onlineTracks === 0) {
        // No tracks available - hide sidebar completely
        get().setVideoPanelSplitPercentage(0);
        get().setShowSidebar(false);
      } else {
        // Tracks are available - show sidebar if currently hidden
        if (get().showSidebar === false) {
          get().setShowSidebar(true);

          // Re-evaluate focused track selection to ensure optimal default
          get().autoSetFocusedTrack(true);

          // Restore default sidebar split percentage
          get().resetVideoPanelSplitPercentage();
        }
      }

      // Update the count of online tracks for layout calculations
      set({
        sidebarTracksAtTime: onlineTracks,
      });
    },

    /**
     * Toggles the playing state of the video and removes any overlay.
     * @param {boolean} isPlaying - The playing state of the video
     */
    setPlaying: (isPlaying, overlay = null) => {
      const video = get().hlsVideoRef?.current;
      if (video) {
        if (isPlaying) {
          set({ playing: true, overlay });
          get().setDuration(video.duration);
        } else {
          video.pause();
          set({ playing: false, overlay });
        }
      }
    },
    /**
     * Sets the playback speed for the video player
     * 
     * This function updates the playback speed, formats the value to ensure consistency,
     * persists it to localStorage for future sessions, and updates the store state.
     * The actual video playback rate is applied by the video component when it detects
     * the playbackSpeed state change.
     * 
     * @param {number|string} playbackSpeed - The desired playback speed multiplier
     *   - Accepts common values like 0.5, 1.0, 2.0, 5.0, 10.0, 16.0
     *   - Can be passed as string or number, will be normalized to number
     *   - Values are validated and formatted by formatSpeedValue()
     * 
     * @example
     * // Set normal speed
     * PlayerStore.setPlaybackSpeed(1.0);
     * 
     * // Set double speed
     * PlayerStore.setPlaybackSpeed(2.0);
     * 
     * // Set from string (will be converted)
     * PlayerStore.setPlaybackSpeed("5.0");
     * 
     * @see formatSpeedValue - For value formatting and validation
     * @see speedOptions - For available speed options
     */
    setPlaybackSpeed: (playbackSpeed, isSmartSpeedChange = false) => {
      // if this speed change is due to smart speed, then we need to just go ahead and set the playback speed and return...
      if (isSmartSpeedChange) {
        set({ playbackSpeed });
        return;
      }

      let _smartSpeedEnabled = false;

      // Format and validate the playback speed value
      playbackSpeed = get().formatSpeedValue(playbackSpeed);

      // Persist to localStorage for future sessions
      localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.PLAYBACK_SPEED, playbackSpeed);

      // if the playback speed is set to 100, this is smart speed so we need to set the smartSpeedPlaybackRanges
      if (playbackSpeed === '100.0') {
        get().calculateSmartSpeedPlaybackRanges();
        _smartSpeedEnabled = true;
      }

      // Update store state (triggers video component to apply new rate)
      set({ playbackSpeed, smartSpeedEnabled: _smartSpeedEnabled });
    },

    /**
     * Sets the suggested playback speed based on network performance and buffer health.
     * 
     * This is used by the dynamic speed suggestion system in HlsVideoPlayer to recommend
     * optimal playback speeds. When Smart Speed is enabled, this suggested speed is used
     * instead of the predefined speed ranges to maximize playback speed while minimizing
     * buffering.
     * 
     * The suggested speed is calculated based on:
     * - Network capacity (how fast segments download)
     * - Buffer health (current buffer level)
     * - Buffer growth rate (how fast buffer is changing)
     * 
     * @param {number} speed - Suggested playback speed (1, 2, 4, 8, 10, 12, or 16)
     * 
     * @example
     * // Called by HlsVideoPlayer's onSuggestedSpeedChange callback
     * PlayerStore.setSuggestedPlaybackSpeed(12); // Suggests 12x speed
     * 
     * @see onSuggestedSpeedChange - HlsVideoPlayer callback that triggers this
     * @see useSmartSpeed - Hook that applies suggested speed when Smart Speed is enabled
     * @see calculateOptimalSpeed - Algorithm that calculates the suggested speed
     */
    setSuggestedPlaybackSpeed: (speed) => {
      if (speed !== get().suggestedPlaybackSpeed) {
        set({ suggestedPlaybackSpeed: speed });
      }
    },

    calculateSmartSpeedPlaybackRanges: () => {
      const ExamSessionStore = get().storeState('examSessionStore');
      const videoDuration = get().duration;
      const flagAnnotations = ExamSessionStore.getAnnotations()
        .filter(annotation => annotation.type?.categoryId === 'flags' && annotation.start > 0)
        .sort((a, b) => a.start - b.start);

      const _speedRanges = [];

      if (flagAnnotations.length === 0) {
        // No flags, everything is 10x speed
        _speedRanges.push({ start: 0, end: videoDuration || Infinity, speed: DEFAULT_SMART_SPEED_PLAYBACK_SPEED });
        set({ smartSpeedPlaybackRanges: _speedRanges });
        return;
      }

      let currentTime = 0;

      let i = 0;
      while (i < flagAnnotations.length) {
        const flag = flagAnnotations[i];
        const flagTime = flag.start;

        // Skip flags that are beyond video duration
        if (flagTime >= videoDuration) {
          break;
        }

        let slowZoneStart = Math.max(0, flagTime - 5); // 5 seconds before
        let slowZoneEnd = Math.min(videoDuration, flagTime + 6); // 6 seconds after, capped at duration

        // Add 10x speed range before this slow zone (if there's a gap)
        if (currentTime < slowZoneStart && currentTime < videoDuration) {
          const rangeEnd = Math.min(slowZoneStart, videoDuration);
          _speedRanges.push({ start: currentTime, end: rangeEnd, speed: DEFAULT_SMART_SPEED_PLAYBACK_SPEED });
        }

        // Check for overlapping zones and merge them
        let j = i + 1;
        while (j < flagAnnotations.length) {
          const nextFlag = flagAnnotations[j];

          // Skip if next flag is beyond duration
          if (nextFlag.start >= videoDuration) {
            break;
          }

          const nextSlowZoneStart = Math.max(0, nextFlag.start - 5);

          if (slowZoneEnd >= nextSlowZoneStart) {
            // Zones overlap, extend the current slow zone
            slowZoneEnd = Math.min(videoDuration, Math.max(slowZoneEnd, nextFlag.start + 6));
            j++; // Include this flag in the current zone
          } else {
            break; // No more overlapping zones
          }
        }

        // Add 1x speed range for the merged slow zone
        const rangeStart = Math.max(currentTime, slowZoneStart);
        const rangeEnd = Math.min(videoDuration, Math.max(rangeStart, slowZoneEnd));

        // Only add if the range is valid and within bounds
        if (rangeStart < rangeEnd && rangeStart < videoDuration) {
          _speedRanges.push({ start: rangeStart, end: rangeEnd, speed: 1, normal: true });
        }
        currentTime = rangeEnd;

        // Move to the next unprocessed flag
        i = j;
      }

      // Add final 10x speed range after the last slow zone
      if (currentTime < videoDuration) {
        _speedRanges.push({ start: currentTime, end: videoDuration, speed: DEFAULT_SMART_SPEED_PLAYBACK_SPEED });
      }

      set({ smartSpeedPlaybackRanges: _speedRanges });
    },

    /**
     * Sets the smart speed enabled state and triggers range calculation if needed
     * @param {boolean} enabled - Whether smart speed should be enabled
     */
    setSmartSpeedEnabled: (enabled) => {
      set({ smartSpeedEnabled: enabled });
      if (enabled) {
        get().calculateSmartSpeedPlaybackRanges();
      } else {
        // Reset to normal speed when disabled
        get().setPlaybackSpeed(1, true);
      }
    },

    /**
     * Sets the visibility state for video labels/overlays
     * 
     * This function controls whether video labels (track names, participant info, etc.)
     * are displayed on top of video tracks. The setting is validated to ensure only
     * valid values are accepted, persisted to localStorage for future sessions,
     * and triggers UI updates across all video components.
     * 
     * @param {string} value - The display state for video labels
     *   - "show": Display video labels on all tracks
     *   - "hide": Hide video labels on all tracks
     *   - Invalid values default to "show" for safety
     * 
     * @example
     * // Show video labels
     * PlayerStore.setDisplayVideoLabels("show");
     * 
     * // Hide video labels
     * PlayerStore.setDisplayVideoLabels("hide");
     * 
     * // Invalid value defaults to "show"
     * PlayerStore.setDisplayVideoLabels("invalid"); // becomes "show"
     * 
     * @see VideoContainer - Component that conditionally renders labels
     * @see AnnotationOptions - UI controls for toggling labels
     * @see usePlayerHotkeys - Keyboard shortcut (s+l) for toggling
     */
    setDisplayVideoLabels: (value) => {
      // Validate input - only allow 'show' or 'hide' as valid values
      if (value !== 'show' && value !== 'hide') {
        value = 'show'; // Default to 'show' for invalid inputs
      }

      // Persist setting to localStorage for future sessions
      localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.DISPLAY_VIDEO_LABELS, value);

      // Update store state (triggers VideoContainer components to show/hide labels)
      set({ displayVideoLabels: value });
    },



    /**
     * Sets the split percentage for the video panel layout
     * 
     * This function controls how the video panel space is divided between different
     * sections (e.g., main video area vs sidebar). The percentage determines the
     * relative size allocation and is persisted to localStorage for future sessions.
     * The value is stored as-is without type conversion despite the comment.
     * 
     * @param {number|string} percentage - The split percentage for the video panel
     *   - Typically a number between 0-100 representing percentage
     *   - Can be passed as string or number (stored as-is)
     *   - Controls the layout proportions in the video player interface
     *   - Common values: 50 (equal split), 70 (main area larger), 30 (sidebar larger)
     * 
     * @example
     * // Equal split between main and sidebar
     * PlayerStore.setVideoPanelSplitPercentage(50);
     * 
     * // Main video area takes 70% of space
     * PlayerStore.setVideoPanelSplitPercentage(70);
     * 
     * // From string input
     * PlayerStore.setVideoPanelSplitPercentage("60");
     * 
     * @see VIDEO_PANEL_SPLIT_PERCENTAGE - localStorage key constant
     * @see videoPanelSplitPercentage - Store state property
     */
    setVideoPanelSplitPercentage: (percentage) => {
      // Note: Despite comment, value is stored as-is without type conversion
      // Persist the split percentage to localStorage for future sessions
      localStorage.setItem(`${CONSTANTS.LOCALSTORAGE_KEYS.VIDEO_PANEL_SPLIT_PERCENTAGE}_${get().storeState('examSessionStore').sessionSid}`, percentage);


      // Update store state (triggers layout components to adjust proportions)
      set({ videoPanelSplitPercentage: percentage });
    },

    /**
     * Resets the video panel split percentage to the default value.
     * This function restores the sidebar width to its default configuration,
     * typically used when the sidebar becomes visible again after being hidden.
     * 
     * The default split percentage is defined in CONSTANTS.PLAYER.DEFAULT_SIDEBAR_PCT
     * and represents the initial sidebar width as a percentage of the total player width.
     * 
     * Side Effects:
     * - Updates the videoPanelSplitPercentage state
     * - Saves the default value to localStorage for persistence
     * - Triggers UI layout recalculation
     * 
     * @function resetVideoPanelSplitPercentage
     * @returns {void} This function does not return a value but updates the split percentage state
     * 
     * @example
     * // Reset sidebar to default width when tracks become available
     * if (tracksAvailable && !sidebarVisible) {
     *   playerStore.resetVideoPanelSplitPercentage();
     * }
     * 
     * @example
     * // Called automatically when sidebar is shown after being hidden
     * playerStore.updateSidebarTrackLayout(); // May internally call resetVideoPanelSplitPercentage
     * 
     * @see {@link setVideoPanelSplitPercentage} For setting custom split percentages
     * @see {@link updateSidebarTrackLayout} For automatic layout management
     */
    resetVideoPanelSplitPercentage: () => {
      get().setVideoPanelSplitPercentage(CONSTANTS.PLAYER.DEFAULT_SIDEBAR_PCT);
    },



    /**
     * Sets the current playback time and handles playback completion logic
     * 
     * This function is the central handler for video time updates. It manages playback
     * completion detection, overlay states, and triggers sidebar layout updates for
     * time-based annotations. The function includes safeguards to prevent time values
     * from exceeding the video duration and handles the transition between playing
     * and completed states.
     * 
     * @param {number} currentTime - The current playback time in seconds
     *   - Should be a positive number representing seconds elapsed
     *   - Values >= duration trigger playback completion
     *   - Values are clamped to not exceed video duration
     *   - Triggers sidebar layout updates for annotation positioning
     * 
     * Key behaviors:
     * - **Completion detection**: When currentTime >= duration, shows completion overlay
     * - **State management**: Stops playback and sets time to exact duration on completion
     * - **Overlay management**: Clears completion overlay when seeking back from end
     * - **Layout updates**: Triggers sidebar track layout updates for annotations
     * - **Change detection**: Only updates if time actually changed to prevent unnecessary renders
     * 
     * @example
     * // Normal time update during playback
     * PlayerStore.setCurrentTime(45.5);
     * 
     * // Time at end triggers completion (if duration is 120s)
     * PlayerStore.setCurrentTime(120);
     * 
     * // Seeking back from completion clears overlay
     * PlayerStore.setCurrentTime(100); // Clears PLAYBACK_COMPLETE overlay
     * 
     * @see OVERLAYS.PLAYBACK_COMPLETE - Completion overlay constant
     * @see updateSidebarTrackLayout - Layout update function for annotations
     * @see setOverlay - Overlay management function
     */
    setCurrentTime: (currentTime) => {
      const duration = get().duration;
      const now = Date.now();

      // Handle playback completion when time reaches or exceeds duration
      if (currentTime >= duration && duration > 0) {
        // Show completion overlay and stop playback
        get().setOverlay(CONSTANTS.OVERLAYS.PLAYBACK_COMPLETE);
        set({
          currentTime: duration, // Clamp to exact duration to prevent overflow
          playing: false // Automatically stop playback at completion
        });
        lastCurrentTimeUpdate = now; // Reset throttle timer
      } else if (get().currentTime !== currentTime) {
        // Dynamic throttling based on playback speed
        // Higher speeds need more frequent updates for smooth UI
        // Skip throttling if:
        // 1. Seeking (dragging is true) - need immediate feedback
        // 2. Throttle interval has passed (dynamic based on speed)
        // 3. Time changed by more than 2 seconds (seeking/jumping)
        const playbackSpeed = get().playbackSpeed === '100.0' ? 1 : parseFloat(get().playbackSpeed);
        const throttleMs = getThrottleInterval(playbackSpeed);
        const timeDelta = Math.abs(currentTime - get().currentTime);
        const shouldUpdate =
          get().dragging ||
          (now - lastCurrentTimeUpdate >= throttleMs) ||
          timeDelta > 2;

        if (shouldUpdate) {
          // Clear completion overlay if seeking back from the end
          if (get().overlay === CONSTANTS.OVERLAYS.PLAYBACK_COMPLETE && currentTime < duration) {
            get().setOverlay(null);
          }

          // Update the current time state
          set({ currentTime });

          // Update sidebar layout for time-based annotations and markers
          get().updateSidebarTrackLayout();

          lastCurrentTimeUpdate = now;
        }
      }
    },


    seekVideoTo: (time) => {
      const video = get().hlsVideoRef?.current;
      if (video) {
        // Don't call video.load() as it breaks HLS blob URLs
        // Just set the currentTime directly
        video.currentTime = time;

        // Clear any completion overlay when seeking
        if (get().overlay === CONSTANTS.OVERLAYS.PLAYBACK_COMPLETE) {
          get().setOverlay(null);
        }
      }
    },
    setOverlay: (overlay) => set({ overlay }),
    setHlsVideoRef: (hlsVideoRef) => set({ hlsVideoRef }),
    setFullScreen: (fullscreen) => set({ fullscreen }),
    setDuration: (duration) => {
      // make sure we round the duration to the nearest whole number
      set({ duration: Math.round(duration) });
    },
    setDragging: (dragging) => set({ dragging }),
    setOnSeek: (onSeek) => set({ onSeek }),
    setOnSeekToLive: (onSeekToLive) => set({ onSeekToLive }),
    setOnSeekToLiveComplete: (onSeekToLiveComplete) =>
      set({ onSeekToLiveComplete }),

    jumpTo: (time) => {
      // Validate time parameter first
      if (!isFinite(time) || time < 0) {
        return;
      }

      // Always switch to recorded mode and clear overlay when jumping
      // This ensures HLS player will mount if we're currently in live mode
      get().setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.RECORDED);
      get().setOverlay(null);

      const video = get().hlsVideoRef?.current;
      const storeDuration = get().duration;

      // If video ref doesn't exist yet (switching from live to recorded),
      // the HLS player will mount and seek to the stored currentTime
      if (!video) {
        // Set the target time so HLS player can seek when it mounts
        get().setCurrentTime(time);
        get().onSeek(time);
        // Set playing state so video auto-plays when HLS player mounts
        get().setPlaying(true);
        return;
      }

      // Video ref exists, seek immediately
      // Use store duration (which is set from HLS metadata) instead of video.duration
      // video.duration can be NaN for HLS streams even when the duration is known
      const duration = storeDuration || video.duration || 0;
      const clampedTime = Math.max(0, Math.min(time, duration));

      // Set seeking state and pause video
      get().setIsSeeking(true);
      video.pause();

      // Don't call video.load() as it breaks HLS blob URLs
      // Just set the currentTime directly
      video.currentTime = clampedTime;

      get().setCurrentTime(clampedTime);
      get().onSeek(clampedTime);

      // Increment seek counter to force component remount after seeking from completion
      set({ seekCounter: get().seekCounter + 1 });

      // adjust the focused track in case we are jumpping to a spot where it needs to be adjsuted...
      // commented this out because it was causing the focused tracks to change when seeking...
      //get().autoSetFocusedTrack(true);

      // Set playing state and start playback
      get().setPlaying(true);
      video.play().catch(error => {
        console.warn('Failed to play video after jump:', error);
        get().setIsSeeking(false);
      });
    },
    setAnnotationContextMenu: (annotation) => {
      set({ annotationContextMenu: annotation });
    },

    toggleMarker: async (id) => {
      const displayMarkers = [...get().displayMarkers];

      const index = displayMarkers.indexOf(id);

      // if it is found, remove it
      if (index > -1) {
        displayMarkers.splice(index, 1);
        get().stores.examSessionStore.getState().clearAnnotationType(id);
      } else {
        await get().stores.examSessionStore.getState().loadAnnotations([id]);
        displayMarkers.push(id);
      }

      // update session storage...
      localStorage.setItem(
        'playbackAnnotations',
        JSON.stringify(displayMarkers)
      );
      set({ displayMarkers });
    },

    formatSpeedValue: (value) => {
      const n = Number(value);
      if (isNaN(n)) {
        return 1;
      }
      return n.toFixed(1);
    },

    /**
     * Updates the player configuration based on session recording status
     * 
     * This function is the central coordinator that configures the player based on whether
     * the session is live or recorded. It reads the session data from the exam session store
     * and sets up the appropriate player state, video source, and timing mechanisms.
     * 
     * **For completed recordings:**
     * - Disables live video capabilities
     * - Sets video source to recorded content
     * - Configures duration from session data
     * - Resets playback to beginning (time 0)
     * 
     * **For live sessions:**
     * - Calculates delay from live stream
     * - Updates "seconds behind live" metric
     * - Starts active session timer for real-time updates
     * 
     * @example
     * // Called when session data changes or player needs reconfiguration
     * PlayerStore.updatePlayer();
     * 
     * // Typically triggered by:
     * // - Session status changes (live → recorded)
     * // - Initial player setup
     * // - Session data updates
     * 
     * @see RECORDING_STATUS.COMPLETE - Status constant for completed recordings
     * @see VIDEO_SOURCES.RECORDED - Video source constant for recorded content
     * @see setCanHaveLiveVideo - Function to control live video capability
     * @see setActiveVideoSource - Function to set video source type
     * @see setSecondsBehindLive - Function to track live stream delay
     * @see startActiveSessionTimer - Function to start real-time updates
     */
    updatePlayer: () => {
      // Get current session data from the exam session store
      const sessionData = get().stores.examSessionStore.getState().sessionData;
      const recordingStatus = sessionData.recordingStatus;
      const endDate = sessionData.recordingEndDate;

      // Configure player for completed recordings
      if (recordingStatus === CONSTANTS.RECORDING_STATUS.COMPLETE) {
        // Disable live video since recording is complete
        get().setCanHaveLiveVideo(false);

        // Switch to recorded video source
        get().setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.RECORDED);

        // Set video duration from session data
        get().setDuration(sessionData.duration);

        // Reset playback to the beginning
        get().setCurrentTime(0);
      } else {
        // Configure player for live sessions

        // Calculate how far behind live we are based on recording end date
        const secondsBehind = moment.utc().diff(endDate, 'seconds');
        get().setSecondsBehindLive(secondsBehind);

        // Start timer for real-time updates of live session metrics
        get().startActiveSessionTimer();
      }
    },
  }));

  // Subscribe to track changes and log them
  // Debug subscription removed - was logging tracks, trackStatusByIdentity, and tracksByIdentity changes

  return store;
};
